import { makeStyles } from '@mui/styles';

import { CARIES, RADIOGRAPHIC_BONELOSS } from '../../../../../constants';
import { useIncipient } from '../../../../../routes/IncipientContext';
import { blueBlack700, white } from '../../../../../theme/videa-style-tokens';
import { isWithinSensitivityRange } from '../../../annotation-utils/incipient';
import type { ImageObject, UserAnnotation } from '../../../types';
import { useIsWithinConfidenceThreshold } from '../../confidenceScoreFilter/helpers';
import PathologyItem from '../pathologyItem/PathologyItem';

import ToothNumberHeader from './toothNumberHeader/ToothNumberHeader';

const useStyles = makeStyles(() => ({
  pathologyItem: {
    borderTop: `1px solid ${blueBlack700}`,
    color: white,
  },
}));

const ToothGroup = (props: PropTypes) => {
  const {
    findingsForTooth,
    findingGroup,
    isFindingTypeVisible,
    isVideaDemoUser = false,
    setAnchorEls,
    toothNumber,
    isPAImage,
    activeImage,
    deleteUserAnnotation,
    highlightFinding,
    updateFinding,
    updateToothNumber,
  } = props;

  const classes = useStyles();
  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();

  const sortedFindingsForTooth = findingsForTooth.sort(
    (findingA: any, findingB: any) => (findingA.type > findingB.type ? -1 : 1),
  );

  // @ts-expect-error TS(2339): Property 'incipientThresholdRange' does not exist ... Remove this comment to see the full error message
  const { incipientThresholdRange } = useIncipient();

  const shouldDisplayPathologyItem = (finding: any) => {
    const { type, side, source } = finding;
    if (type === CARIES && source === 'AI') {
      return (
        isWithinSensitivityRange(
          finding,
          activeImage,
          incipientThresholdRange,
        ) &&
        isWithinConfidenceThreshold(
          finding.type,
          finding.confidenceScore,
          finding?.bin,
        )
      );
    }
    if (type !== RADIOGRAPHIC_BONELOSS) {
      return isWithinConfidenceThreshold(
        finding.type,
        finding.confidenceScore,
        finding?.bin,
      );
    }
    if (type === RADIOGRAPHIC_BONELOSS) {
      const rblSidesToDisplay = ['mesial', 'distal', 'Undefined'];
      return rblSidesToDisplay.includes(side);
    }
    return true;
  };

  const hasVisibleFindings = sortedFindingsForTooth.some((finding) =>
    shouldDisplayPathologyItem(finding),
  );

  if (!hasVisibleFindings) {
    return null;
  }

  return (
    <ToothNumberHeader
      isFindingTypeVisible={isFindingTypeVisible}
      isVideaDemoUser={isVideaDemoUser}
      updateToothNumber={updateToothNumber}
      toothNumber={toothNumber}
      findingGroup={findingGroup}
    >
      {sortedFindingsForTooth.map((finding: any, i: any) => {
        if (shouldDisplayPathologyItem(finding)) {
          const onDeleteClick = (findingToDelete: any) => {
            deleteUserAnnotation(activeImage, findingToDelete);
          };

          const onFindingMouseOver = (findingToHighlight: any) => {
            highlightFinding(findingToHighlight, true);
          };

          const onFindingMouseLeave = (findingToHighlight: any) => {
            highlightFinding(findingToHighlight, false);
          };

          return (
            <div className={classes.pathologyItem} key={i}>
              <PathologyItem
                onFindingMouseOver={onFindingMouseOver}
                onFindingMouseLeave={onFindingMouseLeave}
                onDeleteClick={onDeleteClick}
                finding={finding}
                findingsForTooth={sortedFindingsForTooth}
                updateFinding={updateFinding}
                setAnchorEls={setAnchorEls}
                isPAImage={isPAImage}
              />
            </div>
          );
        }
      })}
    </ToothNumberHeader>
  );
};

type PropTypes = {
  deleteUserAnnotation: (
    image: ImageObject,
    findingToDelete: UserAnnotation | null,
  ) => void;
  findingsForTooth: any[];
  findingGroup: any;
  isFindingTypeVisible: boolean;
  isVideaDemoUser?: boolean;
  setAnchorEls: any;
  toothNumber: any;
  isPAImage: boolean;
  activeImage: any;
  highlightFinding: (findingToHighlight: any, isHovered: boolean) => void;
  updateFinding: () => void;
  updateToothNumber: () => void;
};

export default ToothGroup;
